import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Calendar from '@/components/ui-v2/date/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  formatIsoDate,
  formatLocalizedDate,
  getLocalizedPlaceholder,
  parseDateValue,
  parseLocalizedInputValue,
} from '@/lib/date-utils';
import { cn } from '@/lib/utils';
import { CalendarDays } from 'lucide-react';

export interface TableDateInputProps {
  value?: string | null;
  onDateChange?: (isoValue: string | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
  language?: string;
  placeholder?: string;
  triggerAriaLabel?: string;
}

/**
 * Date input component for table inline editing
 * Provides date picker with calendar popover, styled for table cells
 */
export const TableDateInput = React.forwardRef<
  HTMLInputElement,
  TableDateInputProps
>(
  (
    {
      value,
      onDateChange,
      language: languageProp,
      placeholder,
      className,
      onBlur,
      onKeyDown,
      disabled,
      autoFocus,
      hasError = false,
      triggerAriaLabel,
      ...props
    },
    ref
  ) => {
    const { i18n, t } = useTranslation();
    const language = languageProp ?? i18n.language;
    const sanitizedValue = value ?? null;

    const lastDisplayRef = React.useRef('');
    const blurSkipRef = React.useRef(false);
    const pendingBlurRef = React.useRef(false);

    const [displayValue, setDisplayValue] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);

    React.useEffect(() => {
      const formatted = formatLocalizedDate(sanitizedValue, language);
      setDisplayValue(formatted);
      lastDisplayRef.current = formatted;
    }, [language, sanitizedValue]);

    const commitValue = React.useCallback(() => {
      const candidate = displayValue.trim();
      if (!candidate) {
        if (sanitizedValue !== null) {
          onDateChange?.(null);
        }
        setDisplayValue('');
        lastDisplayRef.current = '';
        return;
      }

      const parsed = parseLocalizedInputValue(candidate, language);
      if (!parsed) {
        setDisplayValue(lastDisplayRef.current);
        return;
      }

      const iso = formatIsoDate(parsed);
      if (!iso) return;

      const formatted = formatLocalizedDate(parsed, language);
      setDisplayValue(formatted);
      lastDisplayRef.current = formatted;

      if (iso !== sanitizedValue) {
        onDateChange?.(iso);
      }
    }, [displayValue, language, onDateChange, sanitizedValue]);

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      commitValue();
      if (blurSkipRef.current) {
        blurSkipRef.current = false;
        pendingBlurRef.current = true;
        return;
      }
      pendingBlurRef.current = false;
      onBlur?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        commitValue();
      }
      if (event.key === 'Escape') {
        setDisplayValue(lastDisplayRef.current);
        setIsOpen(false);
      }
      onKeyDown?.(event);
    };

    const handleSelectDate = (date: Date | undefined) => {
      const normalized = date ?? null;
      if (!normalized) return;

      const iso = formatIsoDate(normalized);
      if (!iso) return;

      const formatted = formatLocalizedDate(normalized, language);
      setDisplayValue(formatted);
      lastDisplayRef.current = formatted;
      setIsOpen(false);

      if (iso !== sanitizedValue) {
        onDateChange?.(iso);
      }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(event.target.value);
    };

    const handleOpenChange = (next: boolean) => {
      if (disabled) return;
      if (!next && pendingBlurRef.current) {
        pendingBlurRef.current = false;
        onBlur?.({} as React.FocusEvent<HTMLInputElement>);
      }
      setIsOpen(next);
    };

    const handleTriggerMouseDown = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
      blurSkipRef.current = true;
    };

    const selectedDate = parseDateValue(sanitizedValue, language);
    const resolvedPlaceholder =
      placeholder ?? getLocalizedPlaceholder(language);
    const triggerLabel = triggerAriaLabel ?? t('general.open_calendar');

    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <div
          className="relative flex h-full w-full items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={ref}
            type="text"
            value={displayValue}
            placeholder={resolvedPlaceholder}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete="off"
            inputMode="numeric"
            className={cn(
              'h-full w-full rounded-none bg-transparent px-2 pr-10 text-sm',
              'focus-visible:outline-none',
              hasError
                ? 'focus-visible:border-red-500 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-red-500'
                : 'focus-visible:border-black focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-black',
              'focus-visible:ring-offset-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              className
            )}
            {...props}
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              aria-label={triggerLabel}
              disabled={disabled}
              onMouseDown={handleTriggerMouseDown}
              onClick={(e) => e.stopPropagation()}
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="h-[360px] p-0" side="bottom" align="end">
          <Calendar
            key={selectedDate?.toISOString() ?? 'default-month'}
            mode="single"
            selected={selectedDate ?? undefined}
            defaultMonth={selectedDate ?? undefined}
            className="h-full"
            onSelect={handleSelectDate}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

TableDateInput.displayName = 'TableDateInput';
